/**
 * Search results utility functions
 * 
 * Provides utilities for fetching, mapping, and processing search results.
 * Handles pagination, provider profile mapping, and image preloading.
 */

import { fetchSearchResults } from '../api/matchmakingApi';
import { mapBackendProvidersToApiResponse, mapApiResponseToMatches } from './matchmakingHelpers';
import { mapBackendProviderToProfile } from './provider-profile.utils';
import { preloadProviderLogos, preloadProviderImages } from './image-preloading.utils';
import { setCachedImages } from './image-cache.utils';
import type { ApiProviderProfile } from '../types/matchmaking.types';
import { PAGE_SIZE } from '../config/matchmaking.config';

/**
 * Extracts providers array from various response formats
 * 
 * Handles different response structures (direct array, object with providers property, etc.)
 * 
 * @param data - Response data in various formats
 * @returns Array of provider objects
 */
export const extractProvidersFromResponse = (data: any): any[] => {
  if (Array.isArray(data.providers)) {
    return data.providers;
  }
  if (data && typeof data === 'object' && 'providers' in data) {
    return Array.isArray((data as any).providers) ? (data as any).providers : [];
  }
  if (Array.isArray(data)) {
    return data;
  }
  return [];
};

/**
 * Fetches and maps provider profiles for a search
 *
 * Fetches paginated results from backend and maps them to provider profile format.
 * Handles pagination calculation based on load count and whether it's a load more operation.
 *
 * FEATURE 021: Supports generic searches with clientId = null
 *
 * @param searchId - ID of the search
 * @param advisorId - ID of the advisor
 * @param clientId - ID of the client (null for generic searches)
 * @param loadCount - Current load count (for pagination)
 * @param isLoadMore - Whether this is a load more operation
 * @returns Map of provider profiles by provider ID
 * @throws Error if fetch fails or status is FAILED
 */
export const fetchAndMapProviderProfiles = async (
  searchId: number,
  advisorId: string,
  clientId: string | null,
  loadCount: number,
  isLoadMore: boolean,
): Promise<Record<string, ApiProviderProfile & { name?: string; serviceArea?: any }>> => {
  const currentLoadCount = isLoadMore ? loadCount + 1 : 1;
  const recordStart = (currentLoadCount - 1) * PAGE_SIZE + 1;
  const recordEnd = currentLoadCount * PAGE_SIZE;

  const backendData = await fetchSearchResults({
    searchId,
    advisorId,
    clientId,
    recordStart,
    recordEnd,
  });

  if (backendData.status && backendData.status.toUpperCase() === 'FAILED') {
    throw new Error(backendData.message || 'Failed to fetch search results from backend.');
  }

  const providerProfilesMap: Record<string, ApiProviderProfile & { name?: string; serviceArea?: any }> = {};

  if (backendData.providers && Array.isArray(backendData.providers)) {
    backendData.providers.forEach((provider: any) => {
      providerProfilesMap[provider.providerId] = mapBackendProviderToProfile(provider);
    });
  } else {
    console.warn('[MATCHMAKING DEBUG] No providers array in backend response!');
  }

  return providerProfilesMap;
};

/**
 * Preloads images for mapped matches and updates state
 * 
 * Preloads provider images and updates the preloaded images state.
 * Logs warnings for providers without images.
 * 
 * @param mappedMatches - Array of mapped match objects
 * @param preloadedImages - Current preloaded images cache
 * @param setIsPreloadingImages - State setter for preloading flag
 * @param setPreloadedImages - State setter for preloaded images map
 */
export const preloadMatchImages = async (
  mappedMatches: any[],
  preloadedImages: Record<string, string | null>,
  setIsPreloadingImages: (value: boolean) => void,
  setPreloadedImages: React.Dispatch<React.SetStateAction<Record<string, string | null>>>,
) => {
  setIsPreloadingImages(true);
  try {
    const imageMap = await preloadProviderImages(mappedMatches, preloadedImages);
    // Update cache with newly loaded images
    setCachedImages(imageMap);
    setPreloadedImages((prev) => {
      const updated = { ...prev, ...imageMap };
      mappedMatches.forEach((match) => {
        const providerId = match.providerProfile?.provider_id;
        if (providerId && !updated[providerId]) {
          console.warn(`No image found for provider ID: ${providerId}`);
        }
      });
      return updated;
    });
  } catch (error) {
    console.error('Error preloading images:', error);
    setPreloadedImages({});
  } finally {
    setIsPreloadingImages(false);
  }
};

/**
 * Processes history results for display
 * 
 * Maps backend providers to API response format, then to matches,
 * preloads logos, and preloads images. Used when loading history items.
 * 
 * @param providers - Array of provider objects from backend
 * @param preloadedImages - Current preloaded images cache
 * @param setIsPreloadingImages - State setter for preloading flag
 * @param setPreloadedImages - State setter for preloaded images map
 * @returns Array of mapped matches ready for display
 */
export const processHistoryResults = async (
  providers: any[],
  preloadedImages: Record<string, string | null>,
  setIsPreloadingImages: (value: boolean) => void,
  setPreloadedImages: React.Dispatch<React.SetStateAction<Record<string, string | null>>>,
) => {
  const apiResponse = mapBackendProvidersToApiResponse(providers);
  let mappedMatches = mapApiResponseToMatches(apiResponse);
  mappedMatches = await preloadProviderLogos(mappedMatches);
  
  await preloadMatchImages(mappedMatches, preloadedImages, setIsPreloadingImages, setPreloadedImages);
  
  return mappedMatches;
};

